import { useEffect, useState, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  User,
  Bell,
  Shield,
  Database,
  Eye,
  EyeOff,
  X,
  UploadIcon,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userProfileApi, type UserProfile } from '@/services/user';
import { uploadFile } from '@/services/upload';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const passwordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type PasswordFormData = z.infer<typeof passwordSchema>;

export function ProfileSettings() {
  const queryClient = useQueryClient();

  // GET profile
  const { data: user } = useQuery<UserProfile>({
    queryKey: ['userProfile'],
    queryFn: userProfileApi.getUserProfile,
  });

  // Local state for form
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    image: '',
  });
  const [initialFormData, setInitialFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    phoneNumber: '',
    image: '',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (user) {
      const userData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        username: user.username || '',
        image: user.image || '',
        phoneNumber: user.phoneNumber || '',
      };
      setFormData(userData);
      setInitialFormData(userData);
    }
  }, [user]);

  // PUT update
  const updateMutation = useMutation({
    mutationFn: userProfileApi.updateUserProfile,
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      toast.success('Profile updated successfully');
      setFormData({
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        username: updatedUser.username,
        phoneNumber: updatedUser.phoneNumber,
        image: updatedUser.image || '',
      });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Update failed');
    },
  });

  const passwordFormHook = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors, isSubmitting: isUpdatingPassword },
  } = passwordFormHook;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const response = await uploadFile(file, (progress) => {
        setUploadProgress(Math.round((progress.loaded / progress.total) * 100));
      });
      console.log(response);

      setFormData((prev) => ({
        ...prev,
        image: response.url,
      }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  console.log(formData);

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: '',
    }));
  };

  const handleSubmit = () => {
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      username: formData.username,
      image: formData.image,
    };
    updateMutation.mutate(payload);
  };

  const handlePasswordChange = async (data: PasswordFormData) => {
    try {
      setPasswordError('');
      setPasswordSuccess('');

      await userProfileApi.changePassword({
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      toast.success('Password updated successfully');
      resetPasswordForm();
      setPasswordSuccess('Password has been updated successfully');
    } catch (error: any) {
      console.error('Password update failed:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to update password';
      setPasswordError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const hasChanges =
    JSON.stringify(formData) !== JSON.stringify(initialFormData);
  const isSubmitDisabled = !hasChanges || isUploading;

  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1>Profile & Settings</h1>
        <Badge variant="outline" className="text-sm">
          Admin
        </Badge>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="data">Data & Backup</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Info */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <span>Profile Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center space-x-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="image">Profile Photo</Label>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <Avatar className="h-20 w-20">
                          <AvatarImage
                            src={`${import.meta.env.VITE_API_STORAGE_URL}${formData?.image}`}
                            alt={`${formData.firstName} ${formData.lastName}`}
                            className="object-cover"
                          />
                          <AvatarFallback>
                            {formData.firstName?.[0]}
                            {formData.lastName?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        {formData.image && formData.image !== '' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-destructive/90 p-0 hover:bg-destructive"
                            onClick={handleRemoveImage}
                            disabled={isUploading}
                          >
                            <X className="h-3 w-3 text-white" />
                            <span className="sr-only">Remove image</span>
                          </Button>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div className="space-y-2 items-center">
                          <input
                            type="file"
                            id="image-upload"
                            ref={fileInputRef}
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileChange}
                            disabled={isUploading}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isUploading}
                          >
                            <UploadIcon className="mr-2 h-4 w-4" />
                            {isUploading ? 'Uploading...' : 'Change photo'}
                          </Button>

                          {isUploading && (
                            <div className="w-48">
                              <Progress
                                value={uploadProgress}
                                className="h-2"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                {uploadProgress}% uploaded
                              </p>
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="w-24  text-red-600 hover:text-red-500"
                          onClick={handleRemoveImage}
                          disabled={isUploading}
                        >
                          <span className="">Remove</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      type="text"
                      value={formData.username}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phoneNumber}
                      // onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select defaultValue="admin" disabled>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="agent">Sales Agent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save changes'}
                </Button>
              </CardContent>
            </Card>

            {/* Account Stats */}
            {/* <Card>
              <CardHeader>
                <CardTitle>Account Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Member since</span>
                  <span className="font-medium">Jan 2024</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Last login</span>
                  <span className="font-medium">Today, 9:30 AM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Leads managed</span>
                  <span className="font-medium">1,247</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Products added</span>
                  <span className="font-medium">89</span>
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label>Preferred Language</Label>
                  <Select defaultValue="en">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select defaultValue="utc-5">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="utc-5">EST (UTC-5)</SelectItem>
                      <SelectItem value="utc-8">PST (UTC-8)</SelectItem>
                      <SelectItem value="utc+0">GMT (UTC+0)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card> */}
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5" />
                <span>Notification Preferences</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center space-y-4 max-w-md">
                <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary/10">
                  <Bell className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">
                  Notifications Coming Soon
                </h3>
                <p className="text-muted-foreground">
                  We're working on bringing you a comprehensive notification
                  system to keep you updated on all important activities.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="data">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Database className="h-5 w-5" />
                <span>Data & Backup</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center space-y-4 max-w-md">
                <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-primary/10">
                  <Database className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold">
                  Data & Backup Coming Soon
                </h3>
                <p className="text-muted-foreground">
                  We're developing powerful data management tools to help you
                  export, backup, and manage your data securely.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="h-5 w-5" />
                  <span>Password & Authentication</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <form
                  onSubmit={handlePasswordSubmit(handlePasswordChange)}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter current password"
                        {...registerPassword('currentPassword')}
                        disabled={isUpdatingPassword}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <p className="text-sm text-red-500">
                        {passwordErrors.currentPassword.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      {...registerPassword('newPassword')}
                      disabled={isUpdatingPassword}
                    />
                    {passwordErrors.newPassword && (
                      <p className="text-sm text-red-500">
                        {passwordErrors.newPassword.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirm New Password
                    </Label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      {...registerPassword('confirmPassword')}
                      disabled={isUpdatingPassword}
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="text-sm text-red-500">
                        {passwordErrors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  {passwordError && (
                    <div className="text-sm text-red-500">{passwordError}</div>
                  )}

                  {passwordSuccess && (
                    <div className="text-sm text-green-500">
                      {passwordSuccess}
                    </div>
                  )}

                  <Button type="submit" disabled={isUpdatingPassword}>
                    {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* <Card>
              <CardHeader>
                <CardTitle>Two-Factor Authentication</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p>Enhance your account security with 2FA</p>
                    <p className="text-sm text-muted-foreground">
                      Currently:{' '}
                      <span className="text-destructive">Disabled</span>
                    </p>
                  </div>
                  <Button>Enable 2FA</Button>
                </div>
              </CardContent>
            </Card> */}

            {/* <Card>
              <CardHeader>
                <CardTitle>Active Sessions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Current Session</p>
                      <p className="text-sm text-muted-foreground">
                        Chrome on Windows • 192.168.1.100
                      </p>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      Active
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">Mobile App</p>
                      <p className="text-sm text-muted-foreground">
                        iOS App • Last seen 2 hours ago
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Revoke
                    </Button>
                  </div>
                </div>
                <Button variant="destructive" className="w-full">
                  Sign Out All Other Sessions
                </Button>
              </CardContent>
            </Card> */}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
